/**
 * StructureLabelsPanel — place structure name badges on any PDF page without measurements.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, MapPin, Plus, X } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { createTakeoffStructure } from '../../utils/pdfTakeoffStructureService';
import type { PdfTakeoffPin, PdfTakeoffStructure, ProposalBuilderData } from '../../types';
import './StructureLabelsPanel.css';

export interface StructureLabelsPanelProps {
  pageNumber: number;
  dealId: string;
  leadId: string;
  userId: string;
  userName: string;
  draftStructures: PdfTakeoffStructure[];
  takeoffPins: PdfTakeoffPin[];
  pinMode: { structureId: string; structureName: string } | null;
  onRequestPin: (structureId: string, structureName: string) => void;
  onCancelPin: () => void;
  onClose: () => void;
}

export function StructureLabelsPanel({
  pageNumber,
  dealId,
  leadId,
  userId,
  userName,
  draftStructures,
  takeoffPins,
  pinMode,
  onRequestPin,
  onCancelPin,
  onClose,
}: StructureLabelsPanelProps) {
  const [newStructureName, setNewStructureName] = useState('');
  const [creatingStructure, setCreatingStructure] = useState(false);
  const [proposalStructures, setProposalStructures] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingProposal, setLoadingProposal] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingProposal(true);
      try {
        const snap = await getDoc(doc(db, 'proposalBuilders', dealId));
        if (cancelled) return;
        if (snap.exists()) {
          const data = snap.data() as ProposalBuilderData;
          setProposalStructures(
            (data.nodeSets || []).map(ns => ({ id: ns.id, name: ns.name || 'Structure' })),
          );
        } else {
          setProposalStructures([]);
        }
      } catch (err) {
        console.error('Error loading proposal structures for labels:', err);
        if (!cancelled) setProposalStructures([]);
      } finally {
        if (!cancelled) setLoadingProposal(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  const structureOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const ns of proposalStructures) {
      seen.set(ns.id, ns.name);
    }
    for (const s of draftStructures) {
      if (s.id) seen.set(s.id, s.name);
    }
    for (const pin of takeoffPins) {
      if (pin.structureId && !seen.has(pin.structureId)) {
        seen.set(pin.structureId, pin.structureName);
      }
    }
    return Array.from(seen.entries()).sort((a, b) =>
      a[1].localeCompare(b[1], undefined, { sensitivity: 'base' }),
    );
  }, [proposalStructures, draftStructures, takeoffPins]);

  const labelsOnPage = useMemo(
    () => takeoffPins.filter(p => p.pageNumber === pageNumber),
    [takeoffPins, pageNumber],
  );

  const handleCreateStructure = async () => {
    const trimmed = newStructureName.trim();
    if (!trimmed) return;
    setCreatingStructure(true);
    try {
      const id = await createTakeoffStructure({
        dealId,
        leadId,
        name: trimmed,
        userId,
        userName,
      });
      setNewStructureName('');
      onRequestPin(id, trimmed);
    } catch (err) {
      console.error('Error creating structure for label:', err);
    } finally {
      setCreatingStructure(false);
    }
  };

  return (
    <div className="structure-labels-panel">
      <div className="structure-labels-header">
        <div>
          <h3>Structure labels</h3>
          <p className="structure-labels-subtitle">
            Name badges for organizing drawings — no measurements required. Page {pageNumber}.
          </p>
        </div>
        <button type="button" className="structure-labels-close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
      </div>

      {pinMode ? (
        <div className="structure-labels-pin-active">
          <MapPin size={14} />
          <span>
            Click the PDF to place <strong>{pinMode.structureName}</strong>
          </span>
          <button type="button" className="structure-labels-cancel-pin" onClick={onCancelPin}>
            Cancel
          </button>
        </div>
      ) : (
        <p className="structure-labels-hint">
          Pick a structure, then click anywhere on the drawing. Use the same structure again for another view or page.
        </p>
      )}

      <div className="structure-labels-add">
        <input
          type="text"
          value={newStructureName}
          onChange={e => setNewStructureName(e.target.value)}
          placeholder="New structure name"
          disabled={!!pinMode}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void handleCreateStructure();
            }
          }}
        />
        <button
          type="button"
          disabled={creatingStructure || !newStructureName.trim() || !!pinMode}
          onClick={() => void handleCreateStructure()}
          title="Create structure and place label"
        >
          {creatingStructure ? <Loader2 size={14} className="spin" /> : <Plus size={14} />}
        </button>
      </div>

      <div className="structure-labels-list">
        {loadingProposal && structureOptions.length === 0 ? (
          <p className="structure-labels-empty">Loading structures…</p>
        ) : structureOptions.length === 0 ? (
          <p className="structure-labels-empty">Add a structure above to start labeling.</p>
        ) : (
          structureOptions.map(([id, name]) => (
            <button
              key={id}
              type="button"
              className={`structure-labels-item ${pinMode?.structureId === id ? 'active' : ''}`}
              disabled={!!pinMode && pinMode.structureId !== id}
              onClick={() => onRequestPin(id, name)}
              title={`Add a "${name}" label on page ${pageNumber}`}
            >
              <MapPin size={12} />
              <span>{name}</span>
            </button>
          ))
        )}
      </div>

      {labelsOnPage.length > 0 && (
        <div className="structure-labels-on-page">
          <span className="structure-labels-on-page-label">
            On this page ({labelsOnPage.length})
          </span>
          <div className="structure-labels-on-page-chips">
            {labelsOnPage.map(pin => (
              <span key={pin.id} className="structure-labels-on-page-chip">
                {pin.structureName}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
